import React, { useState, useEffect, useContext } from "react";
import { Outlet, useLocation } from "react-router-dom";

import Wrapper from "../components/Wrapper";
import Sidebar from "../components/sidebar/Sidebar";
import Main from "../components/Main";
import Navbar from "../components/navbar/Navbar";
import Content from "../components/Content";
import Footer from "../components/Footer";

import dashboardItems from "../components/sidebar/dashboardItems";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWhitelabel,
  getWhitelabelDetails,
} from "../redux/slices/whitelabel";
import NotyfContext from "../contexts/NotyfContext";
import useAuth from "../hooks/useAuth";
import { useWhitelabel } from "../contexts/WhitelabelContext";
import { Alert, Button, Col, Row, Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import useWindowSize from "../hooks/useWindowSize";
import {
  FaHome,
  FaSimCard,
  FaUser,
  FaFile,
  FaExclamationTriangle,
  FaBell,
  FaHistory,
  FaTimesCircle,
  FaCog,
} from "react-icons/fa";
import EmailPromptModal from "../components/dashboard/EmailPromptModal";
import LeadSignUp from "./LeadSignUp";
import FloatingIndicator from "../components/FloatingIndicator";
import useDeviceType from "../hooks/useDeviceType";

const Dashboard = ({ children, demoMode = false }) => {
  const [show, setShow] = useState(false);
  const importState = useSelector((state) => state.sims.importState);
  const user = useSelector((state) => state.user.user);
  const impersonationUser = useSelector(
    (state) => state.impersonationUser.impersonationUser
  );
  const location = useLocation();
  const [activeUser, setActiveUser] = useState({});
  const dispatch = useDispatch();
  const notyf = useContext(NotyfContext);
  const auth = useAuth();
  const loading = demoMode ? false : auth.loading;
  const isAuthenticated = demoMode ? true : auth.isAuthenticated;
  const isInitialized = demoMode ? true : auth.isInitialized;
  const isMfaAuthenticated = demoMode ? true : auth.isMfaAuthenticated;
  const {
    whitelabelLoading,
    error,
    whitelabelUrl,
    backgroundColor,
    fontColor,
    faviconUrl,
  } = useWhitelabel();
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [showLeadSignUp, setShowLeadSignUp] = useState(false);
  const domainCompany = demoMode ? "ClickSims" : activeUser.domainCompany;
  const currentUrl = window.location.hostname;
  const { status, percentage, description } = importState;

  useEffect(() => {
    const shouldShow = status !== null && status !== "done";
    if (shouldShow) {
      setShow(true);
    } else {
      setTimeout(() => {
        setShow(false);
      }, 3000);
    }
  }, [status]);

  useEffect(() => {
    if (
      location.pathname === "/demo" &&
      window.location.origin === "https://platform.clicksims.com"
    ) {
      const script = document.createElement("script");
      script.setAttribute("async", "");
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-6PS31GHPTT";

      const inlineScript = document.createElement("script");
      inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-6PS31GHPTT');
    `;

      document.head.appendChild(script);
      document.head.appendChild(inlineScript);

      return () => {
        document.head.removeChild(script);
        document.head.removeChild(inlineScript);
      };
    }
  }, [location.pathname]);

  useEffect(() => {
    if (demoMode) {
      dispatch(fetchWhitelabel(currentUrl));
    }
  }, [location]);

  const demoDashboardItems = [
    {
      pages: [
        {
          href: "/demo",
          icon: FaHome,
          title: "sidebar.dashboard",
        },
        {
          href: "/demo/crm",
          icon: FaUser,
          title: "sidebar.crm",
          children: [
            {
              href: "/demo/crm/tenants",
              title: "sidebar.tenants",
            },
            {
              href: "/demo/crm/managers",
              title: "sidebar.managers",
            },
            {
              href: "/demo/crm/customers",
              title: "sidebar.customers",
            },
            {
              href: "/demo/crm/subaccounts",
              title: "sidebar.subaccounts",
            },
          ],
        },
        {
          href: "/demo/sims",
          icon: FaSimCard,
          title: "sidebar.sims",
        },
        {
          href: "/demo/account",
          icon: FaUser,
          title: "sidebar.account",
          children: [
            {
              href: "/demo/account/profile",
              title: "sidebar.profile",
            },
            {
              href: "/demo/account/orders",
              title: "sidebar.orders",
            },
            {
              href: "/demo/account/invoices",
              title: "sidebar.invoices",
            },
            {
              href: "/demo/account/billingProducts",
              title: "sidebar.billingProducts",
            },
            {
              href: "/demo/account/statements",
              title: "sidebar.statements",
            },
            {
              href: "/demo/account/emails",
              title: "sidebar.emails",
            },
            {
              href: "/demo/account/whitelist",
              title: "sidebar.whitelist",
            },
            {
              href: "/demo/account/customFields",
              title: "sidebar.customFields",
            },
          ],
        },
        {
          href: "/demo/reports",
          icon: FaFile,
          title: "sidebar.reports",
          children: [
            {
              href: "/demo/reports/single",
              title: "sidebar.generateReport",
              icon: FaFile,
            },
            {
              href: "/demo/reports/automated",
              title: "sidebar.automatedReports",
              icon: FaTimesCircle,
            },
            {
              href: "/demo/reports/history",
              title: "sidebar.reportHistory",
              icon: FaHistory,
            },
          ],
        },
        {
          href: "/demo/alerts",
          icon: FaExclamationTriangle,
          title: "sidebar.alerts",
        },
        {
          href: "/demo/notification",
          icon: FaBell,
          title: "sidebar.notifications",
        },
        {
          href: "/demo/settings",
          icon: FaCog,
          title: "sidebar.settings",
        },
      ],
    },
  ];

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  useEffect(() => {
    const fetchServerDetails = async () => {
      await dispatch(getWhitelabelDetails(Number(activeUser.id))).catch(
        (error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            dismissible: false,
            ripple: true,
            position: {
              x: "center",
              y: "top",
            },
          });
        }
      );
    };

    if (activeUser.id && demoMode === false) {
      fetchServerDetails();
    }
  }, [activeUser, location]);

  useEffect(() => {
    if (demoMode) {
      setShowEmailPrompt(true);
    }
  }, []);

  if (loading || whitelabelLoading) {
    return (
      <React.Fragment>
        <Helmet>
          <link rel="icon" href={faviconUrl} />
        </Helmet>
        <div className="loading-overlay w-100 h-100 d-flex justify-content-center align-items-center">
          <Spinner
            animation="border"
            role="status"
            variant="primary"
            style={{
              "--dynamic-spinner-color": backgroundColor ?? "#0E1A2B",
            }}
          />
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <Wrapper>
        <Helmet
          titleTemplate={`%s | ${domainCompany} - Connectivity Made Easy`}
        >
          <link rel="icon" href={faviconUrl} />
        </Helmet>
        <Sidebar
          items={demoMode === false ? dashboardItems : demoDashboardItems}
          showFooter={false}
        />
        <Main>
          <FloatingIndicator
            show={show}
            status={status}
            description={description}
            percentage={percentage}
          />
          <Navbar demoMode={demoMode} />
          <Content>
            {demoMode && (
              <Row style={{ marginBottom: "1.5rem" }}>
                <Col>
                  <Alert
                    variant="info"
                    style={{
                      padding: "2rem",
                      borderRadius: "12px",
                      boxShadow: "0 0 10px rgba(0,0,0,0.05)",
                      backgroundColor: "#e8f4fb",
                      textAlign: "center",
                      display: "block", // Make sure alert content flows vertically
                    }}
                  >
                    <div style={{ display: "block" }}>
                      <Alert.Heading
                        style={{
                          fontSize: "1.5rem",
                          fontWeight: "600",
                          marginBottom: "1rem",
                          display: "block", // Ensures heading starts on new line
                        }}
                      >
                        Explore the Full Power of Our Platform
                      </Alert.Heading>

                      <p
                        style={{
                          fontSize: "1rem",
                          color: "#333",
                          marginBottom: "1.5rem",
                          display: "block",
                        }}
                      >
                        You're currently in demo mode. To unlock advanced
                        features, real-time data, and full access, please sign
                        up and create your account.
                      </p>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <Button
                          variant="primary"
                          // href="/signup"
                          style={{
                            fontSize: "0.95rem",
                            padding: "0.5rem 1.25rem",
                            fontWeight: "500",
                            borderRadius: "20px",
                            "--dynamic-bg-color": "#0E1A2B",
                            "--dynamic-font-color": "#fff",
                            border: "none",
                          }}
                          onClick={() => setShowLeadSignUp(true)}
                        >
                          Sign Up
                        </Button>
                      </div>
                    </div>
                  </Alert>
                  <LeadSignUp
                    show={showLeadSignUp}
                    onHide={() => setShowLeadSignUp(false)}
                  />
                </Col>
              </Row>
            )}
            {demoMode && (
              <EmailPromptModal
                showEmailPrompt={showEmailPrompt}
                setShowEmailPrompt={setShowEmailPrompt}
              />
            )}
            {children}
            <Outlet />
          </Content>
          {isDesktop && <Footer />}
        </Main>
      </Wrapper>
      {/* <Settings /> */}
    </React.Fragment>
  );
};

export default Dashboard;
